/*
 *                     NOTICE
 *            COPYRIGHT 2024 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */
import { Expression } from "abreact/expression/Expression";
import { GuidedExpressionEditor } from "abreact/expression/ui/editor/GuidedExpressionEditor";
import { AbstractLeafOperandValueImpl } from "abreact/expression/ui/editor/impl/model/impl/row/operandValue/AbstractLeafOperandValueImpl";
import { AbImmutableRecord } from "abreact/utils/AbImmutableRecord";
import { ExpExpressionNodeNodeTypeEnum } from "abweb/generated/abwebinternal/api/dto/ExpExpressionNode";
import { Assert } from "abweb/utils/Assert";
import { ClassUtils } from "abweb/utils/ClassUtils";
import Immutable from "immutable";
// import CATEGORY_SUB_EXPRESSION = Expression.Domain.Constants.CATEGORY_SUB_EXPRESSION;
// import AbstractLeafOperandValue = GuidedExpressionEditor.AbstractModel.AbstractRowModel.AbstractLeafOperandValue;
// import WithoutNodeType = GuidedExpressionEditor.AbstractModel.AbstractRowModel.AbstractOperandValue.WithoutNodeType;
// import LiteralOperandValue = GuidedExpressionEditor.AbstractModel.AbstractRowModel.LiteralOperandValue;
// import ArgumentOperand = GuidedExpressionEditor.ArgumentOperand;

export class LiteralOperandValueImpl extends AbstractLeafOperandValueImpl {
  constructor(stateWithoutNodeType) {
    super({
      expExpressionNodeNodeType: ExpExpressionNodeNodeTypeEnum.LITERAL,
      ...stateWithoutNodeType
    });
  }
  toExpExpressionNode() {
    return this.valueCategoryID === Expression.Domain.Constants.CATEGORY_SUB_EXPRESSION ? this.toSubExpressionRootExpExpressionNode() : super.toExpExpressionNode();
  }

  /**
   * The inner {@linkcode GuidedRowBasedExpressionEditor} of a sub-expression consumes and produces an
   * {@linkcode ExpExpressionDefinition}. Converting its {@linkcode ExpExpressionDefinition#rootNode} back into an
   * {@linkcode OperatorOperandValue} using {@linkcode OperatorOperandExpressionNodeConverter#convert} incurs overhead
   * since it will eventually be re-exported. But, more importantly, since it is primarily invoked during
   * {@linkcode AbstractModel.Mediator#import}, {@linkcode OperatorOperandExpressionNodeConverter#convert} assumes that
   * the condition {@linkcode OperatorOperandValue}s it recursively produces will populate
   * {@linkcode AbstractRowBasedModel#RowModel}s that have additional exporting logic to handle things like
   * the {@linkcode absentSentinelExpOperator}. Given that an {@link Operand.Value.Editor} may only mutate the
   * {@link AbstractModel} by returning an {@link OperandValue}, there is no way at this level to create the necessary
   * {@linkcode AbstractRowBasedModel#RowModel}s. To address this, we can instead serialize and store the
   * sub-expression's {@linkcode ExpExpressionDefinition} in a {@linkcode GuidedExpressionEditor.AbstractModel.AbstractRowModel.LiteralOperandValue}, capitalizing on the
   * export work that the inner {@linkcode GuidedRowBasedExpressionEditor} has already performed.
   *
   * Doing so simplifies the eventual creation of a unified expression tree across arbitrarily many sub-expressions,
   * because each {@linkcode GuidedExpressionEditor.AbstractModel.AbstractRowModel.LiteralOperandValue} that has a {@linkcode AbstractOperandValue#valueCategoryID} of
   * {@linkcode Expression.Domain.Constants.CATEGORY_SUB_EXPRESSION} can then deserialize its stored {@linkcode ExpExpressionDefinition} and replace
   * itself in the tree with the corresponding {@linkcode ExpExpressionDefinition#rootNode}.
   *
   * When this takes place, the {@linkcode GuidedExpressionEditor.AbstractModel.AbstractRowModel.LiteralOperandValue} must essentially merge itself with the hoisted
   * {@linkcode ExpExpressionDefinition#rootNode} by populating {@linkcode ExpExpressionNode#operandName},
   * {@linkcode ExpExpressionNode#categoryID} and {@linkcode ExpExpressionNode#valueTypeIDs} with its own values. These
   * values allow {@linkcode AbstractStandardOperandMultiplexExpressionNodeConverter} to recognize sub-expressions in
   * the expression tree and the framework to associate them with the {@linkcode SubExpressionOperandValueEditor}.
   * However, they should be stripped before consumption by the inner {@linkcode GuidedRowBasedExpressionEditor}, since
   * they have no meaning in the inner level.
   *
   * Here, specifically, we have identified ourselves as a sub-expression {@linkcode GuidedExpressionEditor.AbstractModel.AbstractRowModel.LiteralOperandValue} and perform
   * the hoisting described above.
   *
   * @see {@linkcode AbstractStandardOperandMultiplexExpressionNodeConverter#convertSubExpressionRootExpExpressionNode}
   * @see {@linkcode SubExpressionOperandValueEditor#valueFromProps}
   */
  toSubExpressionRootExpExpressionNode() {
    Assert.assert(this.valueCategoryID === Expression.Domain.Constants.CATEGORY_SUB_EXPRESSION);
    const expExpressionDefinitionJson = LiteralOperandValueImpl.getRequiredPropertyValue(this, "stringValue");
    const {
      rootNode: expExpressionNode
    } = Expression.Definition.parseToMutable(expExpressionDefinitionJson);
    expExpressionNode.operandName = this.operandName;
    expExpressionNode.categoryID = this.valueCategoryID;
    expExpressionNode.valueTypeIDs = this.valueTypeIDs.toArray();
    expExpressionNode.multiple = this.multiple;
    return expExpressionNode;
  }
}
(function (_LiteralOperandValueImpl) {
  function from(stringValue, displayValue, argumentOperandValueEditorProps) {
    const {
      operandName,
      argumentOperandIdentity
    } = argumentOperandValueEditorProps;
    let resolvedValue;
    let resolvedDisplayValue;
    if (argumentOperandIdentity.multiple) {
      const valueElements = stringValue.split(",");
      Assert.assert(valueElements.length > 0);
      const expListValueElements = valueElements.map(element => element?.trim()).filter(element => element).map(element => ({
        value: element
      }));
      resolvedValue = AbImmutableRecord.State.Object.from(expListValueElements);
      resolvedDisplayValue = expListValueElements.map(element => element.value).join(",");
    } else {
      resolvedValue = stringValue;
      resolvedDisplayValue = displayValue;
    }
    const partialState = GuidedExpressionEditor.AbstractModel.AbstractRowModel.AbstractLeafOperandValue.partialState({
      value: resolvedValue,
      displayValue: resolvedDisplayValue
    });
    let {
      operandValue
    } = argumentOperandValueEditorProps;
    if (operandValue == null) {
      return new LiteralOperandValueImpl({
        id: argumentOperandValueEditorProps.generateId(),
        operandName: operandName,
        valueCategoryID: argumentOperandIdentity.valueCategoryID,
        valueTypeIDs: Immutable.List.of(argumentOperandIdentity.valueTypeID),
        multiple: argumentOperandIdentity.multiple ?? false,
        ...partialState
      });
    } else {
      Assert.assert(ClassUtils.isInstanceOf(operandValue, LiteralOperandValueImpl));
      return operandValue.with(partialState);
    }
  }
  _LiteralOperandValueImpl.from = from;
})(LiteralOperandValueImpl || (LiteralOperandValueImpl = {}));
//# sourceMappingURL=LiteralOperandValueImpl.js.map
